import {useTranslation} from "react-i18next";
import type {DocumentPartsResponse, RenderableDocument} from "../types";
import {renderNode} from "./renderNodeLegacy";
import {buildTopicUrl} from "../contentUtils";
import {ExperimentalLink} from "../../../components/ExperimentalLink";
import articleClasses from "../legacy/HtmlArticleLEGACY.module.css";
import panelClasses from "../panel/panelContent.module.css";

type ExcerptGroup = {
    topicId: string;
    name: string;
    topictypeId: string;
    /** Parts with their index in the original response — the index is the
     *  renderNode key prefix and must stay unique across groups. */
    parts: {part: RenderableDocument; index: number}[];
};

// Group excerpts by source topic, preserving first-appearance order. Mirrors
// DibApp2020 slider-group.tsx, minus its cleanGroups() bug (it keys on a
// `sourceTopic.id` the transform never sets, collapsing every part into one
// group). Parts without a topicId get their own group so their body still
// renders — just without a header.
const groupPartsBySourceTopic = (
    parts: RenderableDocument[]
): ExcerptGroup[] => {
    const groups = new Map<string, ExcerptGroup>();

    parts.forEach((part, index) => {
        const key = part.topicId || `part-${index}`;
        let group = groups.get(key);
        if (!group) {
            group = {
                topicId: part.topicId,
                name: part.name,
                topictypeId: part.topictypeId,
                parts: [],
            };
            groups.set(key, group);
        }
        group.parts.push({part, index});
    });

    return Array.from(groups.values());
};

export const PanelDocumentPartsLEGACY = ({
    data,
}: {
    data: DocumentPartsResponse;
}) => {
    const {t} = useTranslation();

    const groups = groupPartsBySourceTopic(data.parts);

    return (
        <div
            className={`${articleClasses.content} ${panelClasses.documentParts}`}
            data-pw="panel-content"
        >
            {groups.map((group, groupIndex) => (
                <div
                    className={panelClasses.excerptGroup}
                    key={group.topicId || `group-${groupIndex}`}
                >
                    {group.name && group.topicId && (
                        <h4
                            className={panelClasses.excerptHeader}
                            data-pw="panel-excerpt-header"
                        >
                            {t("Info.ExcerptFrom", {
                                defaultValue: "Excerpt from",
                            })}{" "}
                            <ExperimentalLink
                                href={buildTopicUrl(
                                    group.topictypeId,
                                    group.name,
                                    group.topicId
                                )}
                            >
                                {group.name}
                            </ExperimentalLink>
                        </h4>
                    )}
                    {group.parts.map(({part, index}) => (
                        <div
                            className={panelClasses.part}
                            key={`part-${index}`}
                        >
                            {renderNode(
                                part,
                                part.root[0].id,
                                {skipAugmentations: true},
                                `part-${index}`
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};
